import React, { useState, useCallback, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../Logo";
import { Button } from "../ui/button";
import FeedbackWidget from "./FeedbackWidget";
import AddCommitmentDialog from "./AddCommitmentDialog";
import NotificationsBell from "./NotificationsBell";
import SpotlightTour from "./SpotlightTour";
import { Home, CalendarDays, Layers, Lightbulb, User, Plus, LogOut } from "lucide-react";

const NAV = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/app/commitments", label: "Commitments", icon: Layers },
  { to: "/app/insights", label: "Insights", icon: Lightbulb },
  { to: "/app/profile", label: "Profile", icon: User },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tourOpen, setTourOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const triggerRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);
  const replayTour = useCallback(() => setTourOpen(true), []);
  const openFeedback = useCallback(() => setFeedbackOpen(true), []);

  useEffect(() => {
    if (user && user.onboarding_complete && !user.tour_completed) setTourOpen(true);
  }, [user]);

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-border bg-white/60 sticky top-0 h-screen p-5">
        <div className="flex flex-col items-start gap-2"><Logo size={34} /><span className="text-[10px] font-semibold tracking-wide text-mint bg-mint-light px-2 py-0.5 rounded-full" data-testid="beta-badge">EARLY ACCESS</span></div>
        <Button onClick={() => setAddOpen(true)} className="rounded-full bg-mint text-white hover:bg-mint/90 mt-8 h-11" data-testid="sidebar-add-btn">
          <Plus size={18} className="mr-1.5" />Add Commitment
        </Button>
        <nav className="mt-6 space-y-1 flex-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors ${isActive ? "bg-navy text-white" : "text-slatey hover:bg-secondary"}`}>
              <n.icon size={18} strokeWidth={1.5} /><span className="text-sm font-medium">{n.label}</span>
            </NavLink>
          ))}
          <NotificationsBell />
        </nav>
        <button onClick={doLogout} className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-slatey hover:bg-secondary transition-colors" data-testid="logout-btn">
          <LogOut size={18} strokeWidth={1.5} /><span className="text-sm font-medium">Sign out</span>
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-30 bg-background/90 backdrop-blur-md border-b border-border pt-safe">
          <div className="px-4 h-14 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <Logo size={28} />
              <span className="text-[9px] font-semibold tracking-wide text-mint bg-mint-light px-1.5 py-0.5 rounded-full shrink-0">BETA</span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <NotificationsBell compact />
              <Button size="sm" onClick={() => setAddOpen(true)} className="rounded-full bg-mint text-white hover:bg-mint/90 h-9 px-3" data-testid="mobile-add-btn">
                <Plus size={16} className="mr-1" />Add
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 md:px-8 py-6 pb-28 md:pb-10 max-w-3xl w-full mx-auto">
          <Outlet context={{ openAdd: () => setAddOpen(true), refreshKey, triggerRefresh, replayTour, openFeedback }} />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-md border-t border-border pb-safe pt-2 px-2 flex justify-around" data-testid="bottom-nav">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`bottomnav-${n.label.toLowerCase()}`}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg ${isActive ? "text-navy" : "text-muted-foreground"}`}>
            {({ isActive }) => (<>
              <n.icon size={20} strokeWidth={1.5} />
              <span className="text-[10px] font-medium">{n.label}</span>
              {isActive && <span className="h-1 w-1 rounded-full bg-mint" />}
            </>)}
          </NavLink>
        ))}
      </nav>

      <FeedbackWidget open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      <AddCommitmentDialog open={addOpen} onOpenChange={setAddOpen} onSaved={triggerRefresh} />
      {tourOpen && <SpotlightTour open={tourOpen} onClose={() => setTourOpen(false)} />}
    </div>
  );
}
